import type { StreamType } from '@/infra'

export type StreamConnectionError =
  | NetworkError
  | HttpError
  | AuthenticationError
  | ParseError
  | PartialConnectionError
  | DisconnectError
  | UnknownError

export interface NetworkError {
  readonly type: 'NETWORK_ERROR'
  readonly message: string
  readonly stream?: StreamType
}

export interface HttpError {
  readonly type: 'HTTP_ERROR'
  readonly statusCode: number
  readonly message: string
  readonly stream?: StreamType
}

export interface AuthenticationError {
  readonly type: 'AUTHENTICATION_ERROR'
  readonly message: string
  readonly stream?: StreamType
}

export interface ParseError {
  readonly type: 'PARSE_ERROR'
  readonly message: string
  readonly stream: StreamType
  readonly rawData?: string
}

export interface PartialConnectionError {
  readonly type: 'PARTIAL_CONNECTION_ERROR'
  readonly connectedStreams: readonly StreamType[]
  readonly failedStreams: readonly StreamType[]
  readonly message: string
}

export interface DisconnectError {
  readonly type: 'DISCONNECT_ERROR'
  readonly message: string
  readonly stream?: StreamType
}

export interface UnknownError {
  readonly type: 'UNKNOWN_ERROR'
  readonly message: string
  readonly stream?: StreamType
  readonly cause?: unknown
}

export function createNetworkError(message: string, stream?: StreamType): NetworkError {
  return { type: 'NETWORK_ERROR', message, ...(stream && { stream }) }
}

export function createHttpError(
  statusCode: number,
  message: string,
  stream?: StreamType,
): HttpError {
  return { type: 'HTTP_ERROR', statusCode, message, ...(stream && { stream }) }
}

export function createAuthenticationError(
  message: string,
  stream?: StreamType,
): AuthenticationError {
  return { type: 'AUTHENTICATION_ERROR', message, ...(stream && { stream }) }
}

export function createParseError(
  message: string,
  stream: StreamType,
  rawData?: string,
): ParseError {
  return { type: 'PARSE_ERROR', message, stream, ...(rawData && { rawData }) }
}

export function createPartialConnectionError(
  connectedStreams: readonly StreamType[],
  failedStreams: readonly StreamType[],
): PartialConnectionError {
  const failedNames = failedStreams.join(', ')
  return {
    type: 'PARTIAL_CONNECTION_ERROR',
    connectedStreams,
    failedStreams,
    message: `Partial connection failure: ${failedNames} stream(s) failed to connect`,
  }
}

export function createDisconnectError(message: string, stream?: StreamType): DisconnectError {
  return { type: 'DISCONNECT_ERROR', message, ...(stream && { stream }) }
}

export function createUnknownError(
  message: string,
  stream?: StreamType,
  cause?: unknown,
): UnknownError {
  return {
    type: 'UNKNOWN_ERROR',
    message,
    ...(stream !== undefined && { stream }),
    ...(cause !== undefined && { cause }),
  }
}

export function isRetriableError(error: StreamConnectionError): boolean {
  switch (error.type) {
    case 'NETWORK_ERROR':
    case 'PARTIAL_CONNECTION_ERROR':
      return true
    case 'HTTP_ERROR':
      return error.statusCode >= 500
    case 'AUTHENTICATION_ERROR':
    case 'PARSE_ERROR':
    case 'DISCONNECT_ERROR':
    case 'UNKNOWN_ERROR':
      return false
  }
}
